import { useState } from "react";
import { View, Text, ScrollView, Pressable } from "react-native";
import { ChevronLeft } from "lucide-react-native";
import { useNavigation } from "@react-navigation/native";
import {
  colors,
  typography,
  spacing,
  GLASS_CARD_STYLE,
  SCREEN_PADDING_TOP,
} from "@louise/moon-ui/theme/tokens";
import StatusBadge from "../../components/StatusBadge";
import ComingSoonOverlay from "../../components/ComingSoonOverlay";
import { ComingSoonSheet } from "../../components/ComingSoonSheet";
import GlassScreenLayout from "@louise/moon-ui/primitives/GlassScreenLayout";

function SectionTitle({ children }: { children: string }) {
  return (
    <Text
      style={{
        color: colors.text,
        fontFamily: typography.families.sansBold,
        fontSize: typography.sizes.xl,
        letterSpacing: -0.3,
      }}
    >
      {children}
    </Text>
  );
}

function PlaceholderCard({
  title,
  subtitle,
}: {
  title: string;
  subtitle: string;
}) {
  return (
    <View
      style={[GLASS_CARD_STYLE, { padding: spacing["2xl"], minHeight: 120 }]}
    >
      <Text
        style={{
          color: colors.text,
          fontFamily: typography.families.sansMedium,
          fontSize: typography.sizes.lg,
        }}
      >
        {title}
      </Text>
      <Text
        style={{
          color: colors.textMid,
          fontFamily: typography.families.sans,
          fontSize: typography.sizes.base,
          marginTop: spacing.sm,
        }}
      >
        {subtitle}
      </Text>
    </View>
  );
}

export default function DevComingSoonScreen() {
  const navigation = useNavigation();
  const [sheetOpen, setSheetOpen] = useState(false);

  return (
    <GlassScreenLayout>
      <ScrollView
        contentContainerStyle={{
          padding: spacing.lg,
          paddingTop: SCREEN_PADDING_TOP + spacing.lg,
          gap: spacing["2xl"],
          maxWidth: 390,
          width: "100%",
          alignSelf: "center",
        }}
      >
        {/* Back button */}
        <Pressable
          onPress={() => navigation.goBack()}
          style={{
            flexDirection: "row",
            alignItems: "center",
            gap: spacing.xs,
          }}
        >
          <ChevronLeft size={20} color={colors.text} />
          <Text
            style={{
              color: colors.text,
              fontFamily: typography.families.sansMedium,
              fontSize: typography.sizes.md,
            }}
          >
            Back
          </Text>
        </Pressable>

        {/* Status Badges */}
        <View style={{ gap: spacing.sm }}>
          <SectionTitle>Status Badges</SectionTitle>
          <View style={{ flexDirection: "row", gap: spacing.sm }}>
            <StatusBadge type="beta" />
            <StatusBadge type="coming-soon" />
          </View>
        </View>

        {/* Coming Soon Overlay (no description) */}
        <View style={{ gap: spacing.sm }}>
          <SectionTitle>Coming Soon Overlay (no description)</SectionTitle>
          <ComingSoonOverlay>
            <PlaceholderCard
              title="Coverage Estimator"
              subtitle="See how much your insurance covers for fertility treatments."
            />
          </ComingSoonOverlay>
        </View>

        {/* Coming Soon Overlay (with description + tap) */}
        <View style={{ gap: spacing.sm }}>
          <SectionTitle>
            Coming Soon Overlay (with description + tap)
          </SectionTitle>
          <ComingSoonOverlay
            description="Launching this summer"
            onPress={() => setSheetOpen(true)}
          >
            <PlaceholderCard
              title="Clinic Finder"
              subtitle="Discover top-rated fertility clinics near you."
            />
          </ComingSoonOverlay>
        </View>

        {/* Coming Soon Sheet trigger */}
        <View style={{ gap: spacing.sm }}>
          <SectionTitle>Coming Soon Sheet</SectionTitle>
          <Pressable
            onPress={() => setSheetOpen(true)}
            style={[
              GLASS_CARD_STYLE,
              {
                padding: spacing["2xl"],
                alignItems: "center",
                justifyContent: "center",
                minHeight: 80,
              },
            ]}
          >
            <Text
              style={{
                color: colors.warm,
                fontFamily: typography.families.sansMedium,
                fontSize: typography.sizes.lg,
              }}
            >
              Tap to open sheet
            </Text>
          </Pressable>
        </View>
      </ScrollView>

      <ComingSoonSheet
        open={sheetOpen}
        onClose={() => setSheetOpen(false)}
        title="Clinic Finder"
        description="We're building a smart clinic finder that matches you with the best fertility clinics based on your location, insurance, and treatment needs."
      />
    </GlassScreenLayout>
  );
}
